import { Link } from '@faceup/router'
import { Tooltip } from '@faceup/ui-base'
import { useElementSize } from '@mantine/hooks'
import type { ComponentProps, ReactNode, Ref } from 'react'
import { twMerge } from 'tailwind-merge'
import { useIsTruncated } from '../hooks/useIsTruncated'
import type { AdminMenuItem as AdminMenuEntry, AdminMenuLogo } from './AdminMenu'
import { AdminMenuItem } from './AdminMenuItem'
import { AdminMenuRailOverflow } from './AdminMenuRailOverflow'
import {
  isRailItemVisible,
  menuItemGap,
  menuItemHeight,
  resolveSecondaryDefault,
  splitRailItems,
} from './adminMenuUtils'
import {
  type AdminMenuActivation,
  type AdminMenuSelection,
  isKeyboardActivation,
} from './useAdminMenuExpansion'

const minGroupGap = 24

type RailGroupProps = {
  items: AdminMenuEntry[]
  isExpanded: boolean
  activeKey?: string
  highlightedKey?: string | null
  onItemHover: (key: string) => void
  onSelect: (selection: AdminMenuSelection) => void
  className?: string
  ref?: Ref<HTMLUListElement>
  children?: ReactNode
}

const RailGroup = ({
  items,
  isExpanded,
  activeKey,
  highlightedKey,
  onItemHover,
  onSelect,
  className,
  ref,
  children,
}: RailGroupProps) => (
  <ul
    ref={ref}
    style={{ gap: menuItemGap }}
    className={twMerge('m-0 flex list-none flex-col p-0', className)}
  >
    {items.map(item => {
      const isActive = item.key === activeKey
      const openPanel = () => onItemHover(item.key)
      const shared = {
        itemKey: item.key,
        label: item.label,
        icon: item.icon,
        isExpanded,
        isActive,
        isHighlighted: item.key === highlightedKey,
        badgeValue: item.badgeValue,
        isNew: item.isNew,
        onMouseEnter: openPanel,
      }
      const onNavigate = (activation: AdminMenuActivation) =>
        onSelect({ ...activation, origin: 'rail', itemKey: item.key })

      if (item.secondary) {
        const target = resolveSecondaryDefault(item.secondary)

        return (
          <li key={item.key} className='shrink-0'>
            {target ? (
              <AdminMenuItem {...shared} hasSecondary onNavigate={onNavigate} link={target.link} />
            ) : (
              <AdminMenuItem
                {...shared}
                hasSecondary
                onClick={openPanel}
                aria-expanded={item.key === highlightedKey}
              />
            )}
          </li>
        )
      }

      return (
        <li key={item.key} className='shrink-0'>
          {item.link ? (
            <AdminMenuItem
              {...shared}
              isCurrentPage={isActive}
              onNavigate={onNavigate}
              link={item.link}
            />
          ) : (
            <AdminMenuItem
              {...shared}
              href={item.href}
              onClick={item.onClick}
              onNavigate={onNavigate}
            />
          )}
        </li>
      )
    })}
    {children}
  </ul>
)

const useRailOverflow = (items: AdminMenuEntry[], hasFooterItems: boolean) => {
  const { ref, height } = useElementSize<HTMLUListElement>()

  if (height === 0) {
    return { ref, visibleItems: [], hiddenItems: [] }
  }

  const available = height - (hasFooterItems ? minGroupGap : 0)
  const maxVisibleCount = Math.floor((available + menuItemGap) / (menuItemHeight + menuItemGap))

  return { ref, ...splitRailItems(items, maxVisibleCount) }
}

export type AdminMenuHoverProps = Pick<
  ComponentProps<'div'>,
  'onMouseEnter' | 'onMouseLeave' | 'onFocus'
>

type AdminMenuRailProps = {
  logo: AdminMenuLogo
  items: AdminMenuEntry[]
  footerItems: AdminMenuEntry[]
  isExpanded: boolean
  hoverProps: AdminMenuHoverProps
  onItemHover: (key: string) => void
  onSelect: (selection: AdminMenuSelection) => void
  activeKey?: string
  highlightedKey?: string | null
}

export const AdminMenuRail = ({
  logo,
  items,
  footerItems,
  isExpanded,
  hoverProps,
  onItemHover,
  onSelect,
  activeKey,
  highlightedKey,
}: AdminMenuRailProps) => {
  const visibleFooterItems = footerItems.filter(isRailItemVisible)
  const { ref: nameRef, isTruncated: isNameTruncated } = useIsTruncated<HTMLSpanElement>()
  const { ref, visibleItems, hiddenItems } = useRailOverflow(
    items.filter(isRailItemVisible),
    visibleFooterItems.length > 0
  )

  const groupProps = { isExpanded, activeKey, highlightedKey, onItemHover, onSelect }

  return (
    <div
      {...hoverProps}
      className={twMerge(
        'absolute inset-y-2 start-0 z-30 flex w-16 flex-col gap-6 overflow-hidden rounded-e-2xl bg-colorBgLayout px-2 pt-2 transition-[width,box-shadow] duration-[340ms] ease-nav motion-reduce:transition-none',
        isExpanded && 'w-65 shadow-navigation'
      )}
    >
      <Tooltip title={isExpanded && isNameTruncated ? logo.name : ''} trigger={['hover', 'focus']}>
        <Link
          to={logo.link}
          className={twMerge(
            'flex shrink-0 items-center rounded-xl focus-visible:outline-2 focus-visible:outline-colorPrimary focus-visible:-outline-offset-2',
            isExpanded && 'gap-3'
          )}
          onClick={event => onSelect({ isKeyboard: isKeyboardActivation(event), origin: 'rail' })}
        >
          <span className='relative flex size-12 shrink-0'>
            <span
              className='flex size-full items-center justify-center overflow-hidden rounded-xl bg-colorPrimary'
              style={{ backgroundColor: logo.backgroundColor }}
            >
              <img
                src={logo.src}
                alt=''
                className={logo.isFullBleed ? 'size-full object-contain' : 'max-h-6 max-w-6'}
              />
            </span>
            {logo.badge && (
              <span
                aria-hidden
                className='absolute end-0 bottom-0 rounded-md border border-colorBorder border-solid bg-colorBgContainer px-1 font-semibold text-colorTextSecondary text-xs'
              >
                {logo.badge}
              </span>
            )}
          </span>
          <span
            className={twMerge(
              'nav-reveal flex min-w-0 flex-col motion-reduce:transition-none',
              isExpanded && 'nav-reveal-open'
            )}
          >
            <span ref={nameRef} className='truncate font-semibold text-base text-colorTextBase'>
              {logo.name}
            </span>
            {logo.caption && (
              <span className='truncate text-colorTextSecondary text-xs'>{logo.caption}</span>
            )}
          </span>
        </Link>
      </Tooltip>
      <div className='flex min-h-0 flex-1 flex-col'>
        <RailGroup
          {...groupProps}
          ref={ref}
          className='min-h-0 flex-1 overflow-hidden'
          items={visibleItems}
        >
          {hiddenItems.length > 0 && (
            <li className='shrink-0'>
              <AdminMenuRailOverflow
                items={hiddenItems}
                isExpanded={isExpanded}
                isActive={hiddenItems.some(item => item.key === activeKey)}
                onItemHover={onItemHover}
                onSelect={onSelect}
              />
            </li>
          )}
        </RailGroup>
        <RailGroup {...groupProps} className='shrink-0' items={visibleFooterItems} />
      </div>
    </div>
  )
}
